import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { API_HEADER_REFRESH_TOKEN } from './constants'
import { API_ACCESS_TOKEN_EXPIRED } from './constants'

export default ({ environment, getAuth0Callbacks }) => {
    /*
     * Check the response for a refresh tokens header, and force refresh the token
     * if the header is present.
     *
     */
    const forceRefreshOnHeader = async (response: AxiosResponse): Promise<void> => {
        if (response?.headers[API_HEADER_REFRESH_TOKEN] === 'true') {
            const { getAccessTokenSilently } = getAuth0Callbacks()
            await getAccessTokenSilently({ ignoreCache: true })
        }
    }

    axios.defaults.baseURL = environment.platformApiBaseUrl
    axios.defaults.withCredentials = true

    axios.interceptors.request.use(
        async (request): Promise<AxiosRequestConfig> => {
            const { getAccessTokenSilently, isAuthenticated } = getAuth0Callbacks()

            // get token using Auth0 SDK, send to API as bearer token
            // we may not be logged in yet
            if (isAuthenticated()) {
                const token = await getAccessTokenSilently()
                request.headers['Authorization'] = `Bearer ${token}`
            }

            // return request
            return request
        },
    )

    axios.interceptors.response.use(
        function(response) {
            // refresh tokens if header given (don't wait)
            forceRefreshOnHeader(response)
            // that's all
            return response
        },
        async function(error: AxiosError) {
            const originalRequest = error.config
            const { isAuthenticated, getAccessTokenSilently, logout } = getAuth0Callbacks()

            // if this was a 401 Unauthenticated and we're allegedly logged in
            if (error?.response?.status === 401 && isAuthenticated()) {
                // we'll need to interrogate this request
                const body = error?.response?.data
                // if this 401 is because of an expired token
                if (body?.cause?.name === API_ACCESS_TOKEN_EXPIRED) {
                    // we can recover by refreshing the token
                    // force a refresh instead of fetching from the cache like usual
                    const token = await getAccessTokenSilently({ ignoreCache: true })
                    // now retry the request with a new refreshed token
                    originalRequest.headers['Authorization'] = `Bearer ${token}`
                    return axios.request(originalRequest)
                }

                // else, this 401 wasn't because of an expired token
                // it must be because we aren't really authenticated, even though we think we are
                // only think left to do is log the user out so we can try again
                logout()
            }

            // if we got a response from the server
            if (error?.response) {
                // refresh tokens if header given (don't wait)
                forceRefreshOnHeader(error.response)
            }

            throw error
        },
    )
}
